import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Crown, User, Shield, Code, Headphones } from 'lucide-react';
import AccessManagement from '../Admin/AccessManagement';

/**
 * Componente de Teste para Verificar Acesso ao AdminDashboard
 * Permite simular login como admin para testar o painel
 */
const AdminTest = () => {
  const { user, login, logout } = useAuth();

  // Função para fazer login como admin de teste
  const loginAsAdmin = async () => {
    try {
      const result = await login('401033888303939584', '22051997');
      if (result.success) {
        console.log('Login como admin realizado com sucesso');
      } else {
        console.error('Erro no login:', result.error);
      }
    } catch (error) {
      console.error('Erro ao fazer login:', error);
    }
  };

  // Função para fazer login como usuário comum
  const loginAsUser = async () => {
    try {
      const result = await login('123456789', 'user123');
      if (result.success) {
        console.log('Login como usuário realizado com sucesso');
      } else {
        console.error('Erro no login:', result.error);
      }
    } catch (error) {
      console.error('Erro ao fazer login:', error);
    }
  };

  // Função para obter ícone do nível de acesso
  const getAccessIcon = (level) => {
    const iconMap = {
      'user': User,
      'suporte': Headphones,
      'moderador': Shield,
      'programador': Code,
      'admin': Crown
    };
    const IconComponent = iconMap[level] || User;
    return <IconComponent className="h-5 w-5" />;
  };

  // Função para obter cor do nível de acesso
  const getAccessColor = (level) => {
    const colorMap = {
      'user': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      'suporte': 'bg-green-500/20 text-green-400 border-green-500/30',
      'moderador': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      'programador': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      'admin': 'bg-red-500/20 text-red-400 border-red-500/30'
    };
    return colorMap[level] || colorMap['user'];
  };

  return (
    <div className="space-y-6 p-6">
      <Card className="bg-black/20 border-white/10">
        <CardHeader>
          <CardTitle className="text-white flex items-center">
            <Crown className="h-6 w-6 mr-2 text-yellow-400" />
            Teste de Acesso - AdminDashboard
          </CardTitle>
          <CardDescription className="text-gray-400">
            Use este componente para testar o acesso ao painel administrativo
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {user ? (
            <div className="space-y-4">
              <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                <h3 className="text-white font-medium mb-2">Usuário Logado</h3>
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    {getAccessIcon(user.accessLevel)}
                    <span className="text-white">{user.name}</span>
                  </div>
                  <Badge className={getAccessColor(user.accessLevel)}>
                    {user.accessLevel}
                  </Badge>
                  <span className="text-gray-400 text-sm">{user.email}</span>
                </div>
              </div>

              <div className="space-y-2">
                <h4 className="text-white font-medium">Permissões de Acesso:</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className={`p-2 rounded ${user.accessLevel === 'admin' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                    AdminDashboard: {user.accessLevel === 'admin' ? 'Permitido' : 'Negado'}
                  </div>
                  <div className="p-2 rounded bg-green-500/20 text-green-400">
                    UserDashboard: Permitido
                  </div>
                </div>
              </div>

              <div className="flex space-x-2">
                <Button
                  onClick={logout}
                  variant="outline"
                  className="border-white/10 text-white hover:bg-white/10"
                >
                  Fazer Logout
                </Button>
              </div>

              {user.accessLevel === 'admin' && (
                <div className="mt-8 pt-8 border-t border-white/10">
                  <AccessManagement />
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-gray-400">Nenhum usuário logado. Escolha uma opção para testar:</p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="bg-white/5 border-white/10">
                  <CardContent className="p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <Crown className="h-5 w-5 text-red-400" />
                      <h4 className="text-white font-medium">Login como Admin</h4>
                    </div>
                    <p className="text-gray-400 text-sm mb-3">
                      Acesso completo ao AdminDashboard
                    </p>
                    <Button
                      onClick={loginAsAdmin}
                      className="w-full bg-red-600 hover:bg-red-700 text-white"
                    >
                      Login Admin
                    </Button>
                  </CardContent>
                </Card>

                <Card className="bg-white/5 border-white/10">
                  <CardContent className="p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <User className="h-5 w-5 text-blue-400" />
                      <h4 className="text-white font-medium">Login como Usuário</h4>
                    </div>
                    <p className="text-gray-400 text-sm mb-3">
                      Acesso apenas ao UserDashboard
                    </p>
                    <Button
                      onClick={loginAsUser}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      Login Usuário
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Informações sobre o Sistema */}
      <Card className="bg-black/20 border-white/10">
        <CardHeader>
          <CardTitle className="text-white">Informações do Sistema</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <h4 className="text-white font-medium mb-2">Credenciais de Teste:</h4>
              <div className="space-y-1 text-gray-400">
                <p><strong>Admin:</strong> Discord ID: 401033888303939584 | Senha: 22051997</p>
                <p><strong>Usuário:</strong> Discord ID: 123456789 | Senha: user123</p>
              </div>
            </div>
            <div>
              <h4 className="text-white font-medium mb-2">Funcionalidades AdminDashboard:</h4>
              <div className="space-y-1 text-gray-400">
                <p>• Gestão completa de usuários</p>
                <p>• Estatísticas e relatórios</p>
                <p>• Configurações do sistema</p>
                <p>• Monitoramento de performance</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminTest;
